import type {
  ChanceEventDef,
  ChanceEventsDef,
  ChanceRulesDef,
  CraftingRulesDef,
  CrewDef,
  FieldKitDef,
  HaulClassRuleDef,
  HoldoutDef,
  HoldoutRulesDef,
  HumanDef,
  KeyPartDef,
  KeyPartRulesDef,
  OccupiedRulesDef,
  OccupiedTellDef,
  PlaceableDef,
  RescueEscortDef,
  RescueFreeDef,
  RescueRadioDef,
  RescueRulesDef,
  RescueScenarioDef,
  RescueTrappedDef,
  RescueWavesDef,
  SiteKindDef,
  TechTierDef,
} from './types';

/*
 * The C# initializers of the Step 2 content classes (packages/shared-cs/Runtime/Content: ChanceDefs, OccupiedDefs, RescueDef, HoldoutDefs,
 * TechDefs, KeyPartDefs). The server parses a bundle into those classes, so a block or field missing from the JSON gets these values
 * there; the section editors show them for a block an old draft doesn't have yet. Every feature defaults to off. Keep them in sync.
 */

export const chanceRulesDefaults = (): Required<ChanceRulesDef> => ({
  enabled: false,
  chancePerRaid: 0.2,
  maxPerRaid: 1,
  maxPerRunDay: 2,
  minRaidsBetween: 2,
  skipFirstRaids: 1,
});

export const chanceEventsDefaults = (): Required<ChanceEventsDef> => ({
  rules: chanceRulesDefaults(),
  events: [],
});

export const chanceEventDefaults = (id = ''): Required<ChanceEventDef> => ({
  id,
  name: '',
  chainId: '',
  weight: 10,
  oncePerRun: false,
  eligibility: { modes: [], squadMin: 1, categories: [] },
  trigger: {
    kind: 'enter_room',
    room: { minAreaM2: 0, notEntry: false },
    minRaidS: 0,
    setPieceId: '',
  },
});

export const occupiedDefaults = (): Required<OccupiedRulesDef> => ({
  enabled: false,
  chancePerDestination: 0.12,
  minPerSet: 1,
  maxPerSet: 2,
  minRooms: 6,
  categories: [],
  zombieMult: {},
  trueTells: { twoChance: 0.75, herringChance: 0.4 },
  stances: {},
  tradeOffers: 3,
  fairPriceMult: 0.8,
  maxTrades: 3,
  maxRecruits: 1,
  maxHumansAlive: 6,
  stashRolls: 3,
  // S2-HUM's additions.
  gateChainId: '',
  talkChainId: '',
  surrenderChainId: '',
  dispositionPriceMult: {},
  madPriceMin: 0.5,
  madPriceMax: 1.5,
  captiveRoleId: '',
  moraleAllyDown: 35,
  moraleLowHealth: 25,
  lowHealthFrac: 0.4,
  surrenderAt: 30,
  lowCoverBlock: 0.6,
  lowCoverBlockCrouched: 0.9,
  zombieIgnoreM: 3,
  talkRingM: 1.5,
});

export const crewDefaults = (id = ''): Required<CrewDef> => ({
  id,
  name: '',
  osmTags: [],
  dispositions: {},
  headcount: [],
  roster: [],
  tradeTableId: '',
  stashTableId: '',
  priceMult: 0,
  lines: {},
});

export const humanDefaults = (id = ''): Required<HumanDef> => ({
  id,
  role: '',
  health: 70,
  weaponId: '',
  meleeDamage: 0,
  shotDamage: 10,
  telegraphS: 0.7,
  fireIntervalS: 1.6,
  hitNear: 0.7,
  nearM: 6,
  hitFar: 0.35,
  farM: 15,
  sightHalfAngleDeg: 35,
  sightRangeM: 12,
  walkMps: 2.2,
  runMps: 3.2,
  morale: 100,
  moraleFloor: 0,
  headshotMult: 0.8,
  headArmor: false,
  knockbackResist: 0,
  coverSeek: false,
  armorId: '',
  // WIND (contract 3.5): a hostile human's gun shot that lands is a wound.
  weaponWounds: true,
});

export const occupiedTellDefaults = (id = ''): Required<OccupiedTellDef> => ({
  id,
  gate: '',
  map: false,
  disposition: '',
  text: '',
  panelProp: '',
});

export const rescueRadioDefaults = (): Required<RescueRadioDef> => ({
  callsPerDayByLevel: [],
  shortHandedRosterAtMost: 2,
  shortHandedCallsPerDay: 1,
  callMinutes: 180,
  autoSpawn: false,
  autoSpawnGapMinutes: 60,
});

export const rescueEscortDefaults = (): Required<RescueEscortDef> => ({
  speedMult: 0.8,
  injuredSpeedMult: 0.6,
  leashM: 8,
  targetWeight: 1,
  unstickS: 4,
});

export const rescuesDefaults = (): Required<RescueRulesDef> => ({
  enabled: false,
  energySurcharge: 10,
  successCooldownHours: 24,
  successesPer7Days: 3,
  claimHoldHours: 24,
  traitChance: 0.5,
  minFreeMult: 0.5,
  waveWarnS: 5,
  radio: rescueRadioDefaults(),
  escort: rescueEscortDefaults(),
  namePools: {},
  scenarios: [],
});

export const rescueWavesDefaults = (): Required<RescueWavesDef> => ({
  firstAtS: 5,
  everyS: 20,
  baseSize: 3,
  growth: 1,
  perExtraMember: 1,
  finalExtraZombieId: '',
  spawnTag: '',
  escortEveryS: 0,
  escortSize: 0,
});

export const rescueTrappedDefaults = (): RescueTrappedDef => ({
  slot: 'main',
  namePool: '',
  roleId: '',
  traitId: null,
  startHealth: 100,
  // WIND (contract 3.7): the rescued survivor's start tier (startHealth stays: the escort NPC's HP).
  startTier: 'winded',
  injuredChance: 0,
  epitaph: '',
});

export const rescueScenarioDefaults = (id = ''): Required<RescueScenarioDef> => ({
  id,
  title: '',
  categoryIds: [],
  osmTagsAny: [],
  scenarioId: '',
  setPieceRoomId: '',
  free: { kind: '', seconds: 60, toolTag: '', toolMult: 1, secondSeconds: 0 } as RescueFreeDef,
  waves: rescueWavesDefaults(),
  trapped: [],
  beats: { briefing: '', twists: [] },
  bait: { humanIds: [], minRaiders: 2, maxRaiders: 3, anchor: '' },
});

export const holdoutDefaults = (id = ''): Required<HoldoutDef> => ({
  id,
  name: '',
  categories: [],
  minFootprintM2: 0,
  setPiece: '',
  setupS: 90,
  breatherS: 20,
  entries: [],
  waves: [],
  placementLimits: {},
  squadScale: [],
});

export const holdoutRulesDefaults = (): Required<HoldoutRulesDef> => ({
  liveCap: { low: 18, mid: 28, high: 40 },
  corpseCap: 12,
  areaCost: {},
  spillQueue: 8,
  stragglers: { count: 3, afterS: 20 },
  doorBashHp: 15,
});

export const placeableDefaults = (kind = 'barricade'): Required<PlaceableDef> => ({
  kind,
  footprint: [],
  facing: false,
  arcDeg: 0,
  rangeM: 0,
  tripM: 0,
  damage: 0,
  falloffMin: 0,
  knockbackM: 0,
  backBlastM: 0,
  armS: 0,
  placeS: 1,
  noiseRadiusM: 0,
  friendlyFireMult: 0,
  hp: 0,
  slowMult: 1,
  dps: 0,
  wear: 0,
  lightRadiusM: 0,
  navArea: '',
});

export const techTierDefaults = (tier = 0): Required<TechTierDef> => ({
  tier,
  name: '',
  gateModuleId: '',
  keyParts: [],
  milestones: [],
  questMissionId: '',
  huntDifficultyAdd: 0,
  rebuild: [],
});

export const craftingDefaults = (): Required<CraftingRulesDef> => ({
  craftSecondsByTier: [],
  buildOrders: { maxRunning: 1, maxWaiting: 3, maxSteps: 40, maxCrafts: 200, maxQuantity: 20 },
  workshopNewHours: 48,
  startRequiresPresence: true,
  power: { fuelItemId: 'fuel_can', whPerFuel: 2000 },
  room: { toolWall: [] },
});

export const fieldKitDefaults = (): Required<FieldKitDef> => ({ recipeIds: [], maxTimes: 5 });

export const siteKindDefaults = (id = ''): Required<SiteKindDef> => ({
  id,
  name: '',
  osmTags: [],
  category: '',
  inPersonAllowed: true,
});

export const keyPartDefaults = (id = ''): Required<KeyPartDef> => ({
  id,
  tier: 0,
  name: '',
  missionTitle: '',
  pieces: [],
  siteKinds: [],
  fallbackCategories: [],
  virtualSiteIds: [],
  scenarioId: '',
  setPieceId: '',
  hint: '',
  beats: [],
});

const haulClass = (o: HaulClassRuleDef): Required<HaulClassRuleDef> => ({
  speed: 1,
  pickupS: 0,
  noiseM: 0,
  everyS: 0,
  ...o,
});

export const keyPartRulesDefaults = (): Required<KeyPartRulesDef> => ({
  hardLimitPct: 150,
  farRadiusM: 3000,
  siteTtlHours: 72,
  rerollHours: 24,
  lostPieceRespawnHours: 6,
  haul: {
    heavy: haulClass({ speed: 0.85, pickupS: 1 }),
    twoPerson: haulClass({ speed: 0.6, pickupS: 1.5 }),
    drag: haulClass({ speed: 0.35, pickupS: 1.5, noiseM: 6, everyS: 1 }),
  },
  virtualSites: [],
});

/** The Step 2 sections, for the section editor (`teamsSectionDefaults`). */
export const s2SectionDefaults = {
  chanceEvents: chanceEventsDefaults,
  occupied: occupiedDefaults,
  rescues: rescuesDefaults,
  holdoutRules: holdoutRulesDefaults,
  crafting: craftingDefaults,
  fieldKit: fieldKitDefaults,
  keyPartRules: keyPartRulesDefaults,
} as const;
